import { Database } from 'lucide-react';
import { PlusIcon, TrashIcon } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSecrets } from '@/pages/credentials/hooks/useSecrets';
import {
  secretData,
  secretTypeLabel,
  STORAGE_TYPE_OPTIONS,
  endpointPlaceholder,
} from '@/lib/secretStorage';
import type { StorageTypeCase } from '@/lib/secretStorage';
import {
  emptyMount,
  MAX_MOUNTS,
  normalizeBucketPath,
  normalizeMountPath,
  type CredentialSource,
  type InlineCredentialDraft,
  type MountDraft,
} from './storageMount';

interface Props {
  enabled: boolean;
  onEnabledChange: (v: boolean) => void;
  mounts: MountDraft[];
  onMountsChange: (v: MountDraft[]) => void;
}

function IconStorage() {
  return <Database className="w-5 h-5" strokeWidth={1.5} />;
}

function SectionCard({
  id,
  icon,
  title,
  subtitle,
  action,
  children,
}: {
  id: string;
  icon: React.ReactNode;
  title: React.ReactNode;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-20">
      <div className="rounded-xl border border-border bg-background shadow-sm overflow-hidden">
        <div className="h-0.5 bg-gradient-to-r from-primary/80 via-primary/40 to-transparent" />
        <div className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0">
              {icon}
            </div>
            <div className="flex-1">
              <h2 className="text-base font-semibold text-foreground">{title}</h2>
              {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
            </div>
            {action}
          </div>
          {children}
        </div>
      </div>
    </section>
  );
}

function Switch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ${checked ? 'bg-primary' : 'bg-input'}`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-background shadow-sm ring-0 transition-transform duration-200 translate-y-0.5 ${checked ? 'translate-x-5' : 'translate-x-0.5'}`}
      />
    </button>
  );
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-lg bg-muted/40 p-1 ring-1 ring-inset ring-border/50">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
            value === o.value
              ? 'bg-background text-foreground shadow-sm ring-1 ring-black/5 dark:ring-white/10'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <Label className="text-sm font-medium">
      {children} {required && <span className="text-destructive">*</span>}
    </Label>
  );
}

function ReadonlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input value={value} disabled className="h-9" />
    </div>
  );
}

function UnavailableCredentialWarning() {
  return (
    <div className="flex items-start gap-2 rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-xs text-destructive">
      <svg
        className="w-3.5 h-3.5 mt-0.5 shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v3m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
        />
      </svg>
      <span>当前引用的凭证已被删除或不可用，请重新选择凭证后保存。</span>
    </div>
  );
}

interface CredentialDraft {
  credentialSource: CredentialSource;
  secretId: string;
  inlineCredential: InlineCredentialDraft;
}

function CredentialEditor({
  credential,
  onChange,
}: {
  credential: CredentialDraft;
  onChange: (patch: Partial<CredentialDraft>) => void;
}) {
  const { data: secretsRes } = useSecrets(1, 100);
  const secrets = secretsRes?.data ?? [];
  const selected = secrets.find((s) => s.id === credential.secretId);
  const selectedBody = selected ? secretData(selected) : undefined;

  const isUnavailable =
    credential.credentialSource === 'existing' &&
    !!credential.secretId &&
    secrets.length > 0 &&
    !selected;

  const setInline = (patch: Partial<InlineCredentialDraft>) =>
    onChange({ inlineCredential: { ...credential.inlineCredential, ...patch } });

  return (
    <div
      className={`rounded-lg border bg-muted/20 p-4 space-y-4 ${isUnavailable ? 'border-destructive/60 ring-1 ring-destructive/30' : ''}`}
    >
      <div>
        <FieldLabel required>访问凭证</FieldLabel>
        <p className="text-xs text-muted-foreground mt-1">所有挂载点共用同一个对象存储凭证</p>
      </div>

      {isUnavailable && <UnavailableCredentialWarning />}

      <Segmented<CredentialSource>
        value={credential.credentialSource}
        onChange={(v) => onChange({ credentialSource: v })}
        options={[
          { value: 'existing', label: '选择已有凭证' },
          { value: 'inline', label: '新建凭证' },
        ]}
      />

      {credential.credentialSource === 'existing' ? (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <FieldLabel required>对象存储凭证</FieldLabel>
            <select
              value={credential.secretId}
              onChange={(e) => onChange({ secretId: e.target.value })}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            >
              <option value="">请选择凭证</option>
              {secrets.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} · {secretData(s)?.accessKey ?? ''}
                </option>
              ))}
            </select>
          </div>
          {selected && (
            <div className="grid grid-cols-2 gap-3">
              <ReadonlyField label="存储类型" value={secretTypeLabel(selected)} />
              <ReadonlyField label="AccessKey ID" value={selectedBody?.accessKey ?? ''} />
              <ReadonlyField label="Region" value={selectedBody?.region ?? ''} />
              <ReadonlyField label="Endpoint" value={selectedBody?.endpoint ?? ''} />
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <FieldLabel required>凭证名称</FieldLabel>
              <Input
                value={credential.inlineCredential.name}
                maxLength={64}
                placeholder="例如：生产数据存储"
                onChange={(e) => setInline({ name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <FieldLabel required>存储类型</FieldLabel>
              <select
                value={credential.inlineCredential.storageType}
                onChange={(e) => setInline({ storageType: e.target.value as StorageTypeCase })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                {STORAGE_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <FieldLabel required>Region</FieldLabel>
              <Input
                value={credential.inlineCredential.region}
                maxLength={128}
                placeholder="例如：cn-hangzhou"
                onChange={(e) => setInline({ region: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <FieldLabel required>Endpoint</FieldLabel>
              <Input
                value={credential.inlineCredential.endpoint}
                maxLength={512}
                placeholder={endpointPlaceholder(credential.inlineCredential.storageType)}
                onChange={(e) => setInline({ endpoint: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <FieldLabel required>AccessKey ID</FieldLabel>
              <Input
                value={credential.inlineCredential.accessKey}
                autoComplete="off"
                placeholder="请输入 AccessKey ID"
                onChange={(e) => setInline({ accessKey: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <FieldLabel required>Secret Access Key</FieldLabel>
              <Input
                type="password"
                value={credential.inlineCredential.secretKey}
                autoComplete="new-password"
                placeholder="请输入 Secret Access Key"
                onChange={(e) => setInline({ secretKey: e.target.value })}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            新建的凭证会保存到「对象存储凭证」管理列表，供后续复用。
          </p>
        </div>
      )}
    </div>
  );
}

function MountCard({
  index,
  mount,
  total,
  onChange,
  onRemove,
}: {
  index: number;
  mount: MountDraft;
  total: number;
  onChange: (patch: Partial<MountDraft>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="rounded-lg border bg-muted/20 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">挂载点 {index + 1}</span>
        {total > 1 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
            onClick={onRemove}
          >
            <TrashIcon className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <FieldLabel required>Bucket</FieldLabel>
          <Input
            value={mount.bucket}
            maxLength={128}
            placeholder="company-data"
            onChange={(e) => onChange({ bucket: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <FieldLabel required>Bucket 子目录</FieldLabel>
          <Input
            value={mount.bucketPath}
            maxLength={512}
            placeholder="/datasets"
            onChange={(e) => onChange({ bucketPath: e.target.value })}
            onBlur={(e) => onChange({ bucketPath: normalizeBucketPath(e.target.value) })}
          />
        </div>
        <div className="space-y-1.5">
          <FieldLabel required>本地目录</FieldLabel>
          <Input
            value={mount.mountPath}
            placeholder="/home/user/data"
            onChange={(e) => onChange({ mountPath: e.target.value })}
            onBlur={(e) => onChange({ mountPath: normalizeMountPath(e.target.value) })}
          />
        </div>
        <div className="space-y-1.5">
          <FieldLabel required>权限</FieldLabel>
          <Segmented<'ro' | 'rw'>
            value={mount.readOnly ? 'ro' : 'rw'}
            onChange={(v) => onChange({ readOnly: v === 'ro' })}
            options={[
              { value: 'ro', label: '只读' },
              { value: 'rw', label: '读写' },
            ]}
          />
        </div>
        <div className="space-y-1.5 col-span-2">
          <FieldLabel>备注</FieldLabel>
          <Input
            value={mount.remark}
            maxLength={200}
            placeholder="用途说明（可选）"
            onChange={(e) => onChange({ remark: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
}

function credentialOf(mounts: MountDraft[]): CredentialDraft {
  const first = mounts[0] ?? emptyMount();
  return {
    credentialSource: first.credentialSource,
    secretId: first.secretId,
    inlineCredential: first.inlineCredential,
  };
}

export default function StorageMountSection({
  enabled,
  onEnabledChange,
  mounts,
  onMountsChange,
}: Props) {
  const credential = credentialOf(mounts);

  const applyCredential = (patch: Partial<CredentialDraft>) => {
    const next = { ...credential, ...patch };
    onMountsChange(
      mounts.map((m) => ({
        ...m,
        credentialSource: next.credentialSource,
        secretId: next.secretId,
        inlineCredential: next.inlineCredential,
      }))
    );
  };

  const setMount = (i: number, patch: Partial<MountDraft>) =>
    onMountsChange(mounts.map((m, j) => (j === i ? { ...m, ...patch } : m)));

  const addMount = () =>
    onMountsChange([
      ...mounts,
      {
        ...emptyMount(),
        credentialSource: credential.credentialSource,
        secretId: credential.secretId,
        inlineCredential: credential.inlineCredential,
      },
    ]);

  return (
    <SectionCard
      id="storage"
      icon={<IconStorage />}
      title="存储挂载"
      subtitle="挂载企业自有 S3 兼容对象存储，实例释放后不会删除其中的数据"
      action={<Switch checked={enabled} onChange={onEnabledChange} />}
    >
      {!enabled ? (
        <p className="text-xs text-muted-foreground">未开启对象存储挂载</p>
      ) : (
        <div className="space-y-4">
          <CredentialEditor credential={credential} onChange={applyCredential} />
          {mounts.map((m, i) => (
            <MountCard
              key={i}
              index={i}
              mount={m}
              total={mounts.length}
              onChange={(patch) => setMount(i, patch)}
              onRemove={() => onMountsChange(mounts.filter((_, j) => j !== i))}
            />
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={mounts.length >= MAX_MOUNTS}
            onClick={addMount}
          >
            <PlusIcon className="h-3.5 w-3.5 mr-1" />
            新增挂载点 ({mounts.length}/{MAX_MOUNTS})
          </Button>
        </div>
      )}
    </SectionCard>
  );
}
